/**
 * S020-8.20.5: ReviewPanel
 *
 * Displays activity stats and pending decisions for automation review.
 *
 * Features:
 * - 30-day activity chart
 * - Pending decisions list with disambiguation UI
 * - Inline actions (resolve/skip)
 */

import { Button } from '@affine/component/ui/button';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WorkspaceService } from '../../../workspace';
import {
  filterDocGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '../../mail/mail-folder-visibility-filter';
import { AutomationDiscoveryService } from '../../services/automation-discovery';
import { DatabaseDiscoveryService } from '../../services/database-discovery';
import type {
  ActivityStats,
  DailyActivity,
  PendingDecision,
} from '../../services/review';
import { ReviewService } from '../../services/review';
import * as styles from './review-panel.css';

// =============================================================================
// ActivityChart Component
// =============================================================================

interface ActivityChartProps {
  stats: ActivityStats | null;
  isLoading: boolean;
}

const ActivityChart: FC<ActivityChartProps> = ({ stats, isLoading }) => {
  const t = useI18n();
  if (isLoading) {
    return (
      <div className={styles.activitySection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            {t['com.streamlain.streamlain.review.activity']()}
          </span>
        </div>
        <div className={styles.loadingState}>
          {t['com.streamlain.streamlain.common.loading']()}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Get max runs for scaling
  const maxRuns = Math.max(...stats.daily.map(d => d.runs), 1);

  // Only show last 14 days for compact display
  const recentDays = stats.daily.slice(-14);

  return (
    <div className={styles.activitySection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t['com.streamlain.streamlain.review.activity']()}
        </span>
        <span className={styles.sectionSubtitle}>
          {t['com.streamlain.streamlain.review.last-30-days']()}
        </span>
      </div>

      {/* Mini bar chart */}
      <div className={styles.chartContainer}>
        {recentDays.map((day: DailyActivity) => {
          const height = (day.runs / maxRuns) * 100;
          const hasIssues = day.issues > 0;
          return (
            <div
              key={day.date}
              className={styles.chartBar}
              style={{ height: `${Math.max(height, 4)}%` }}
              data-has-issues={hasIssues}
              title={`${day.date}: ${day.runs} runs${hasIssues ? `, ${day.issues} issues` : ''}`}
            />
          );
        })}
      </div>

      {/* Stats summary */}
      <div className={styles.statsSummary}>
        <span>{stats.totalRuns} runs</span>
        <span className={styles.statsDivider}>|</span>
        <span>{stats.successRate}% success</span>
        <span className={styles.statsDivider}>|</span>
        <span>{stats.pendingCount} pending</span>
      </div>
    </div>
  );
};

// =============================================================================
// ExpandableCandidateRow Component
// =============================================================================

interface ExpandableCandidateRowProps {
  candidate: { id: string; title: string; context?: string };
  index: number;
  onUse: (candidateId: string) => void;
}

const ExpandableCandidateRow: FC<ExpandableCandidateRowProps> = ({
  candidate,
  index,
  onUse,
}) => {
  const t = useI18n();
  const [expanded, setExpanded] = useState(false);

  // Parse context: can be JSON object string or plain string
  const contextEntries = useMemo(() => {
    if (!candidate.context) return null;
    if (typeof candidate.context === 'object') {
      return Object.entries(
        candidate.context as Record<string, unknown>
      ).filter(([, v]) => v != null);
    }
    try {
      const parsed = JSON.parse(candidate.context);
      if (typeof parsed === 'object' && parsed !== null) {
        return Object.entries(parsed).filter(([, v]) => v != null);
      }
    } catch {
      // plain string context
    }
    return null;
  }, [candidate.context]);

  const hasDetails = contextEntries && contextEntries.length > 0;

  return (
    <div className={styles.candidateRow}>
      <div
        className={styles.candidateHeader}
        onClick={() => hasDetails && setExpanded(prev => !prev)}
      >
        <div className={styles.candidateHeaderLeft}>
          {hasDetails && (
            <span className={styles.candidateChevron} data-expanded={expanded}>
              &#9654;
            </span>
          )}
          <span className={styles.candidateTitle}>{candidate.title}</span>
          <span className={styles.candidateIndex}>#{index + 1}</span>
        </div>
        <Button
          size="default"
          className={styles.candidateUseButton}
          onClick={e => {
            e.stopPropagation();
            onUse(candidate.id);
          }}
        >
          {t['com.streamlain.streamlain.review.use']()}
        </Button>
      </div>
      {expanded && hasDetails && (
        <div className={styles.candidateDetails}>
          <div className={styles.candidateFieldList}>
            {contextEntries.map(([key, value]) => (
              <div key={key} className={styles.candidateField}>
                <span className={styles.candidateFieldKey}>{key}</span>
                <span className={styles.candidateFieldValue}>
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PendingDecisionCard Component
// =============================================================================

interface PendingDecisionCardProps {
  decision: PendingDecision;
  docTitle: string;
  automationName: string;
  columnNameMap: Map<string, string>;
  onResolve: (decisionId: string, resolvedWith: string) => void;
  onSkip: (decisionId: string) => void;
}

const PendingDecisionCard: FC<PendingDecisionCardProps> = ({
  decision,
  docTitle,
  automationName,
  columnNameMap,
  onResolve,
  onSkip,
}) => {
  const t = useI18n();
  const timeAgo = useMemo(() => {
    const now = Date.now();
    const created = new Date(decision.createdAt).getTime();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return t['com.streamlain.streamlain.review.just-now']();
  }, [decision.createdAt, t]);

  const columnLabel =
    columnNameMap.get(decision.targetColumn) ??
    decision.targetColumnName ??
    decision.targetColumn;
  const matchLabel =
    decision.matchCount === 0
      ? t['com.streamlain.streamlain.review.no-matches']()
      : t['com.streamlain.streamlain.review.possible-matches']({
          count: String(decision.matchCount),
        });

  const handleUse = useCallback(
    (candidateId: string) => onResolve(decision.id, candidateId),
    [decision.id, onResolve]
  );

  return (
    <div className={styles.decisionCard} data-decision-id={decision.id}>
      {/* Doc + Automation context */}
      <div className={styles.decisionDocLine}>
        <span>
          {docTitle}
          {automationName ? ` / ${automationName}` : ''}
        </span>
        <span className={styles.decisionTime}>{timeAgo}</span>
      </div>

      {/* Search description */}
      <div className={styles.decisionSearch}>
        "{decision.searchValue}" &rarr; {columnLabel}
      </div>

      {/* Match count */}
      <div className={styles.decisionMeta}>{matchLabel}</div>

      {/* Expandable candidate rows */}
      {decision.candidates.length > 0 && (
        <div className={styles.candidateList}>
          {decision.candidates.map((candidate, idx) => (
            <ExpandableCandidateRow
              key={candidate.id}
              candidate={candidate}
              index={idx}
              onUse={handleUse}
            />
          ))}
        </div>
      )}

      {/* No matches message */}
      {decision.matchCount === 0 && (
        <div className={styles.noMatchMessage}>
          {t['com.streamlain.streamlain.review.no-matching-records']()}
        </div>
      )}

      {/* Skip action */}
      <div className={styles.decisionActions}>
        <Button
          size="default"
          variant="plain"
          onClick={() => onSkip(decision.id)}
        >
          {t['com.streamlain.streamlain.review.skip']()}
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// ReviewPanel Component
// =============================================================================

export const ReviewPanel: FC<{ hideActivityChart?: boolean }> = ({
  hideActivityChart,
}) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const reviewService = useService(ReviewService);
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const discoveryService = useService(AutomationDiscoveryService);

  const databaseDiscoveryService = useService(DatabaseDiscoveryService);

  const workspaceId = workspaceService.workspace.id;
  const automationsByDoc = useLiveData(
    discoveryService.canvasAutomationsByDoc$
  );
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleAutomationsByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        automationsByDoc,
        hiddenMailFolderDocIds
      ),
    [automationsByDoc, hiddenMailFolderDocIds]
  );
  const databases = useLiveData(databaseDiscoveryService.databases$);

  const pendingDecisions = useLiveData(reviewService.pendingDecisions$);
  const activityStats = useLiveData(reviewService.activityStats$);
  const isLoading = useLiveData(reviewService.isLoading$);

  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'success' | 'info';
  } | null>(null);

  // Fetch data on mount
  useEffect(() => {
    reviewService.refreshAll(workspaceId).catch(console.error);
  }, [reviewService, workspaceId]);

  // sl-djze.3: Scroll to + highlight a specific decision card on navigate event
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const { pendingDecisionId } = (
        e as CustomEvent<{ pendingDecisionId: string }>
      ).detail;
      if (!pendingDecisionId) return;
      // Small delay to allow DOM render after tab switch
      requestAnimationFrame(() => {
        const el = panelRef.current?.querySelector(
          `[data-decision-id="${CSS.escape(pendingDecisionId)}"]`
        ) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add(styles.decisionHighlight);
          setTimeout(() => el.classList.remove(styles.decisionHighlight), 3000);
        }
      });
    };
    window.addEventListener('pending-decision-review', handler);
    return () => window.removeEventListener('pending-decision-review', handler);
  }, []);

  // Filter only pending decisions
  const pending = useMemo(
    () =>
      pendingDecisions.filter(
        d =>
          d.status === 'pending' &&
          (!d.docId || !hiddenMailFolderDocIds.has(d.docId))
      ),
    [hiddenMailFolderDocIds, pendingDecisions]
  );

  // Build blockId → automationName lookup from local BlockSuite state
  const automationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of visibleAutomationsByDoc) {
      for (const automation of group.automations) {
        map.set(automation.blockId, automation.name);
      }
    }
    return map;
  }, [visibleAutomationsByDoc]);

  // Build columnId → columnName lookup from local database metadata
  const columnNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const db of databases) {
      for (const col of db.columns) {
        map.set(col.id, col.name);
      }
    }
    return map;
  }, [databases]);

  const handleResolve = useCallback(
    (decisionId: string, resolvedWith: string) => {
      const decision = pending.find(d => d.id === decisionId);
      const candidate = decision?.candidates.find(c => c.id === resolvedWith);

      reviewService
        .resolveDecision(workspaceId, decisionId, resolvedWith)
        .then(success => {
          if (success) {
            setFeedback({
              message: t['com.streamlain.streamlain.review.linked-to']({
                item: candidate?.title || 'selected item',
              }),
              type: 'success',
            });
            setTimeout(() => setFeedback(null), 3000);
          }
        })
        .catch(console.error);
    },
    [pending, reviewService, t, workspaceId]
  );

  const handleSkip = useCallback(
    (decisionId: string) => {
      reviewService
        .skipDecision(workspaceId, decisionId)
        .then(success => {
          if (success) {
            setFeedback({
              message: t['com.streamlain.streamlain.review.skipped'](),
              type: 'info',
            });
            setTimeout(() => setFeedback(null), 3000);
          }
        })
        .catch(console.error);
    },
    [reviewService, t, workspaceId]
  );

  return (
    <div className={styles.reviewPanel} ref={panelRef}>
      {/* Activity Chart */}
      {!hideActivityChart && (
        <ActivityChart stats={activityStats} isLoading={isLoading} />
      )}

      {/* Pending Decisions */}
      <div className={styles.pendingSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            {t['com.streamlain.streamlain.review.pending-decisions']()}
            {pending.length > 0 && (
              <span className={styles.pendingBadge}>{pending.length}</span>
            )}
          </span>
          <span className={styles.sectionSubtitle}>
            {t['com.streamlain.streamlain.review.needs-input']()}
          </span>
        </div>

        {/* Feedback */}
        {feedback && (
          <div className={styles.feedback} data-type={feedback.type}>
            {feedback.type === 'success' ? '[OK]' : '[i]'} {feedback.message}
          </div>
        )}

        {/* Decision Cards or Empty State */}
        {pending.length > 0 ? (
          <div className={styles.decisionList}>
            {pending.map(decision => (
              <PendingDecisionCard
                key={decision.id}
                decision={decision}
                docTitle={
                  decision.docId
                    ? docDisplayMetaService.title$(decision.docId).value ||
                      'Untitled'
                    : 'Untitled'
                }
                automationName={
                  decision.blockId
                    ? (automationNameMap.get(decision.blockId) ?? '')
                    : ''
                }
                columnNameMap={columnNameMap}
                onResolve={handleResolve}
                onSkip={handleSkip}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>--</div>
            <div className={styles.emptyTitle}>
              {t['com.streamlain.streamlain.review.all-caught-up']()}
            </div>
            <div className={styles.emptySubtitle}>
              {t['com.streamlain.streamlain.review.empty-subtitle']()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
